import { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import EditorialMarquee from "@/components/EditorialMarquee";
import HowItWorks from "@/components/HowItWorks";
import EarningsCalculator from "@/components/EarningsCalculator";
import SecurityTrust from "@/components/SecurityTrust";
import FAQAccordion from "@/components/FAQAccordion";
import Testimonials from "@/components/Testimonials";
import FinalCTA from "@/components/FinalCTA";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import PartnerOffer from "@/components/PartnerOffer";
import VideoSection from "@/components/VideoSection";
import Dashboard from "@/pages/Dashboard";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import NotFound from "@/pages/NotFound";
import { fetchClicks, registerClick, getCachedClicks, cacheClicks } from "@/lib/clicks";
import { trackAffiliateClick } from "@/lib/analytics";
import { analytics } from "@heycatch/sdk";

const AFFILIATE_URL = "https://join.honeygain.com/GEOKAC09DC/";

function Landing() {
  const [clicks, setClicks] = useState(() => getCachedClicks());

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothTouch: false });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    fetchClicks().then((count) => {
      if (count != null) setClicks(count);
    });
  }, []);

  const handleJoin = useCallback((source = "landing") => {
    trackAffiliateClick(source);
    analytics.trackEvent("affiliate_join_click", { location: source });
    window.open(AFFILIATE_URL, "_blank", "noopener,noreferrer");
    registerClick().then((count) => {
      if (count != null) {
        setClicks(count);
      } else {
        setClicks((prev) => {
          const next = (prev ?? 1427) + 1;
          cacheClicks(next);
          return next;
        });
      }
    });
  }, []);

  return (
    <div className="App font-body bg-[#FAF8F5] text-zinc-900 antialiased">
      <Header clicks={clicks} onJoin={handleJoin} />
      <main>
        <HeroSection clicks={clicks} onJoin={handleJoin} />
        <EditorialMarquee />
        <HowItWorks />
        <VideoSection onJoin={handleJoin} />
        <EarningsCalculator onJoin={handleJoin} />
        <SecurityTrust />
        <Testimonials />
        <FAQAccordion />
        <FinalCTA clicks={clicks} onJoin={handleJoin} />
        <PartnerOffer />
      </main>
      <Footer onJoin={handleJoin} />
      <div className="h-24 lg:hidden" aria-hidden />
      <StickyMobileCTA onJoin={handleJoin} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/stats" element={<Dashboard />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/about" element={<About />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
